import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, Eye, Pencil, Trash2, RefreshCw, TrendingDown, TrendingUp, Minus, CheckCircle } from 'lucide-react';
import { listingsAPI, photoAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const STATUS_KEYS = {
  active: 'mylist.status_active', sold: 'mylist.status_sold', cancelled: 'mylist.status_cancelled', pending: 'mylist.status_pending', rejected: 'mylist.status_rejected',
};
const STATUS_COLORS = {
  active: 'bg-green-100 text-green-700', sold: 'bg-blue-100 text-blue-700', cancelled: 'bg-gray-100 text-gray-700',
  pending: 'bg-amber-100 text-amber-700', rejected: 'bg-red-100 text-red-700',
};

// Calculate days since date
function daysSince(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  return Math.floor((now - d) / (1000 * 60 * 60 * 24));
}

// Price level component
function PriceLevel({ price, marketPrice }) {
  const { t } = useTranslation();
  if (!marketPrice || marketPrice <= 0) return null;
  
  const ratio = price / marketPrice;
  let icon, text, textColor;
  
  if (ratio <= 0.85) {
    icon = <TrendingDown className="w-3 h-3" />;
    text = t('mylist.very_good_price');
    textColor = 'text-green-600';
  } else if (ratio <= 0.95) {
    icon = <TrendingDown className="w-3 h-3" />;
    text = t('mylist.good_price');
    textColor = 'text-lime-600';
  } else if (ratio <= 1.05) {
    icon = <Minus className="w-3 h-3" />;
    text = t('mylist.fair_price');
    textColor = 'text-yellow-600';
  } else if (ratio <= 1.15) {
    icon = <TrendingUp className="w-3 h-3" />;
    text = t('mylist.high_price');
    textColor = 'text-orange-600';
  } else {
    icon = <TrendingUp className="w-3 h-3" />;
    text = t('mylist.very_high_price');
    textColor = 'text-red-600';
  }
  
  // Calculate bar position (0-100%)
  const position = Math.min(Math.max((ratio - 0.7) / 0.6 * 100, 0), 100);
  
  return (
    <div className="mt-2 px-1">
      <div className="flex items-center justify-between text-[10px] text-[#5A8BA8] mb-1">
        <span>{t('mylist.market', { price: marketPrice })}</span>
        <span className={`flex items-center gap-0.5 font-medium ${textColor}`}>
          {icon} {text}
        </span>
      </div>
      <div className="h-1.5 bg-gradient-to-r from-green-400 via-yellow-400 to-red-400 rounded-full relative">
        <div 
          className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-white border-2 border-[#033871] rounded-full shadow"
          style={{ left: `calc(${position}% - 5px)` }}
        />
      </div>
    </div>
  );
}

export default function MyListingsTab({ listings, setListings, loading }) {
  const { t } = useTranslation();
  const [editingListing, setEditingListing] = useState(null);
  const [editForm, setEditForm] = useState({ price: '', description: '' });
  const [confirmingId, setConfirmingId] = useState(null);

  const handleDeleteListing = async (listingId) => {
    if (!window.confirm(t('mylist.confirm_delete'))) return;
    try {
      await listingsAPI.delete(listingId);
      toast.success(t('mylist.deleted'));
      const l = await listingsAPI.getMine();
      setListings(l.data);
    } catch (err) { toast.error(err.response?.data?.detail || t('mylist.error')); }
  };

  const handleConfirmAvailability = async (listingId) => {
    setConfirmingId(listingId);
    try {
      await listingsAPI.confirmAvailability(listingId);
      toast.success(t('mylist.availability_confirmed_toast'));
      const l = await listingsAPI.getMine();
      setListings(l.data);
    } catch (err) { 
      toast.error(err.response?.data?.detail || t('mylist.error')); 
    } finally {
      setConfirmingId(null);
    }
  };

  const handleEditListing = (listing) => {
    setEditingListing(listing.id);
    setEditForm({ price: String(listing.price), description: listing.description || '' });
  };

  const handleSaveListing = async (listingId) => {
    try {
      await listingsAPI.update(listingId, { price: parseFloat(editForm.price), description: editForm.description });
      toast.success(t('mylist.updated'));
      setEditingListing(null);
      const l = await listingsAPI.getMine();
      setListings(l.data);
    } catch (err) { toast.error(err.response?.data?.detail || t('mylist.error')); }
  };

  if (loading) return <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-[#D6E8F0] rounded-xl animate-pulse" />)}</div>;

  if (listings.length === 0) return (
    <div className="text-center py-16">
      <Package className="w-12 h-12 text-[#B8D4E3] mx-auto mb-4" />
      <p className="text-[#5A8BA8] mb-4">{t('mylist.no_listings')}</p>
      <Link to="/vendre"><Button className="bg-[#32CD32] hover:bg-[#28a428]">{t('mylist.publish')}</Button></Link>
    </div>
  );

  return (
    <div className="space-y-3">
      {listings.map(l => (
        <div key={l.id} className="bg-white rounded-xl border border-[#D6E8F0] p-3 sm:p-4" data-testid={`my-listing-${l.id}`}>
          <Link to={`/annonce/${l.id}`} className="flex items-center gap-3 sm:gap-4 hover:opacity-80 transition-opacity">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg bg-[#E8F4FD] overflow-hidden shrink-0">
              {l.photos?.[0] && <img src={photoAPI.getUrl(l.photos[0])} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm sm:text-base text-[#033871] truncate">{l.brand} {l.model}</p>
              <div className="flex items-center gap-2 mt-0.5">
                <p className="text-base sm:text-lg font-bold text-[#0C63BF]">{l.price.toFixed(0)} &euro;</p>
                <span className="flex items-center gap-1 text-[10px] sm:text-xs text-[#5A8BA8]"><Eye className="w-3 h-3" />{l.views}</span>
              </div>
            </div>
            <span className={`px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium shrink-0 ${STATUS_COLORS[l.status] || 'bg-gray-100'}`}>
              {STATUS_KEYS[l.status] ? t(STATUS_KEYS[l.status]) : l.status}
            </span>
          </Link>
          {editingListing === l.id ? (
            <div className="mt-3 pt-3 border-t border-[#E8F4FD] space-y-3" data-testid={`edit-listing-form-${l.id}`}>
              <div>
                <label className="text-xs font-medium text-[#5A8BA8]">{t('mylist.price_label')}</label>
                <input type="number" value={editForm.price} onChange={e => setEditForm(p => ({ ...p, price: e.target.value }))}
                  className="w-full h-9 px-3 mt-1 rounded-lg border border-[#D6E8F0] text-sm focus:border-[#0C63BF] outline-none" data-testid={`edit-price-${l.id}`} />
              </div>
              <div>
                <label className="text-xs font-medium text-[#5A8BA8]">{t('mylist.description')}</label>
                <textarea value={editForm.description} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))}
                  className="w-full h-20 px-3 py-2 mt-1 rounded-lg border border-[#D6E8F0] text-sm resize-none focus:border-[#0C63BF] outline-none" data-testid={`edit-desc-${l.id}`} />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => handleSaveListing(l.id)} className="bg-[#32CD32] hover:bg-[#28a428]" data-testid={`save-listing-${l.id}`}>{t('mylist.save')}</Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingListing(null)}>{t('mylist.cancel')}</Button>
              </div>
            </div>
          ) : l.status === 'rejected' ? (
            <div className="mt-3 pt-3 border-t border-red-100">
              <div className="bg-red-50 rounded-lg p-3" data-testid={`rejection-reason-${l.id}`}>
                <p className="text-xs font-semibold text-red-600 mb-1">{t('mylist.rejection_reason')}</p>
                <p className="text-sm text-red-700">{l.rejection_reason || t('mylist.not_compliant')}</p>
              </div>
              <p className="text-xs text-[#5A8BA8] mt-2">{t('mylist.rejected_hint')}</p>
            </div>
          ) : l.status === 'pending' ? (
            <div className="mt-3 pt-3 border-t border-amber-100">
              <p className="text-xs text-amber-600 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                {t('mylist.pending_review')}
              </p>
            </div>
          ) : l.status === 'active' && (
            <div className="mt-3 pt-3 border-t border-[#E8F4FD]">
              {/* Price level bar */}
              <PriceLevel price={l.price} marketPrice={l.market_price} />
              
              {/* Availability confirmation */}
              {(() => {
                const days = daysSince(l.availability_confirmed_at);
                const needsConfirmation = days === null || days >= 7;
                return (
                  <div className={`flex items-center justify-between mt-3 p-2 rounded-lg ${needsConfirmation ? 'bg-amber-50' : 'bg-green-50'}`}>
                    <div className="flex items-center gap-2">
                      {needsConfirmation ? (
                        <>
                          <RefreshCw className="w-4 h-4 text-amber-600" />
                          <span className="text-xs text-amber-700">
                            {days === null ? t('mylist.confirm_availability') : t('mylist.confirmed_ago', { days })}
                          </span>
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span className="text-xs text-green-700">
                            {days === 0 ? t('mylist.confirmed_today') : t('mylist.confirmed_days', { days })}
                          </span>
                        </>
                      )}
                    </div>
                    <Button 
                      size="sm" 
                      onClick={() => handleConfirmAvailability(l.id)}
                      disabled={confirmingId === l.id}
                      className={`text-xs h-7 px-3 ${needsConfirmation ? 'bg-amber-500 hover:bg-amber-600' : 'bg-green-500 hover:bg-green-600'}`}
                      data-testid={`confirm-availability-${l.id}`}
                    >
                      {confirmingId === l.id ? (
                        <RefreshCw className="w-3 h-3 animate-spin" />
                      ) : (
                        t('mylist.confirm')
                      )}
                    </Button>
                  </div>
                );
              })()}
              
              {/* Action buttons */}
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => handleEditListing(l)} className="gap-1 text-xs" data-testid={`edit-listing-${l.id}`}>
                  <Pencil className="w-3 h-3" /> {t('mylist.edit')}
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleDeleteListing(l.id)} className="gap-1 text-xs text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600" data-testid={`delete-listing-${l.id}`}>
                  <Trash2 className="w-3 h-3" /> {t('mylist.delete')}
                </Button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
